"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { NAV_SECTIONS, NAV_LINKS, type NavEntry } from "@/lib/nav";
import { Icon } from "@/components/Icon";

type GroupEntry = Extract<NavEntry, { kind: "group" }>;

/** 現在パスに最も一致する（＝最長一致の）href を求める。 */
function activeHref(pathname: string): string | null {
  let best: string | null = null;
  for (const it of NAV_LINKS) {
    const match = it.href === "/" ? pathname === "/" : pathname === it.href || pathname.startsWith(`${it.href}/`);
    if (match && (best === null || it.href.length > best.length)) best = it.href;
  }
  return best;
}

/** グループが現在のアクティブ子リンクを含むか。 */
function groupHasActive(entry: NavEntry, active: string | null): boolean {
  return entry.kind === "group" && entry.children.some((c) => c.href === active);
}

export function Sidebar() {
  const pathname = usePathname();
  const active = activeHref(pathname);

  const asideRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // freee型：カテゴリにホバー/クリックすると右隣にサブメニューのパネルがせり出す。
  // anchorRight〜left の間はボタンとパネルの隙間（デッドゾーン）。ここにブリッジ要素を敷いて、
  // 斜めにカーソルを動かした際にonMouseLeaveが誤発火してパネルが消えるのを防ぐ。
  const [flyout, setFlyout] = useState<{ entry: GroupEntry; top: number; left: number; anchorRight: number } | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setFlyout(null), 300);
  };
  const openFlyout = (entry: GroupEntry, el: HTMLElement) => {
    cancelClose();
    const asideRect = asideRef.current?.getBoundingClientRect();
    const btnRect = el.getBoundingClientRect();
    const anchorRight = btnRect.right;
    setFlyout({ entry, top: btnRect.top, left: asideRect ? asideRect.right : btnRect.right, anchorRight });
  };

  return (
    <aside
      ref={asideRef}
      className="relative flex h-full w-60 shrink-0 flex-col border-r"
      style={{ background: "var(--sidebar)", borderColor: "var(--border)" }}
    >
      {/* ロゴ */}
      <div className="flex items-center gap-2 px-4 py-3.5">
        <span className="grid h-7 w-7 place-items-center rounded-md text-[13px] font-bold" style={{ background: "var(--accent)", color: "#fff" }}>
          会
        </span>
        <span className="text-[16.5px] font-bold" style={{ color: "var(--accent-strong)" }}>会計ローカル</span>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {NAV_SECTIONS.map((section, si) => {
          // タイトルなしセクション（先頭を除く）は freee 同様に上に薄い区切り線を入れる。
          const needsDivider = !section.title && si > 0;
          return (
          <div
            key={si}
            className={section.title ? "mt-3" : needsDivider ? "mt-3 border-t pt-3" : "mt-1"}
            style={needsDivider ? { borderColor: "var(--border)" } : undefined}
          >
            {section.title && (
              <div className="px-3 pb-1 pt-1 text-[11.5px] font-semibold tracking-wider" style={{ color: "var(--text-faint)" }}>
                {section.title}
              </div>
            )}
            <div className="flex flex-col gap-0.5">
              {section.entries.map((entry) => {
                if (entry.kind === "link") {
                  const isActive = active === entry.href;
                  return (
                    <Link
                      key={entry.href}
                      href={entry.href}
                      onMouseEnter={() => {
                        cancelClose();
                        setFlyout(null);
                      }}
                      className="flex items-center gap-2.5 rounded-md px-3 py-2 text-[14.5px] font-medium transition-colors"
                      style={{
                        color: isActive ? "var(--accent-strong)" : "var(--text-muted)",
                        background: isActive ? "var(--accent-soft)" : "transparent",
                      }}
                    >
                      <Icon name={entry.icon} className="opacity-90" />
                      <span>{entry.label}</span>
                    </Link>
                  );
                }
                const hasActive = groupHasActive(entry, active);
                const isOpen = flyout?.entry.label === entry.label;
                return (
                  <button
                    key={entry.label}
                    type="button"
                    onMouseEnter={(e) => openFlyout(entry, e.currentTarget)}
                    onMouseLeave={scheduleClose}
                    onClick={(e) => (isOpen ? setFlyout(null) : openFlyout(entry, e.currentTarget))}
                    className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-[14.5px] font-medium transition-colors"
                    style={{
                      color: hasActive || isOpen ? "var(--accent-strong)" : "var(--text-muted)",
                      background: isOpen ? "var(--accent-soft)" : "transparent",
                    }}
                  >
                    <Icon name={entry.icon} className="opacity-90" />
                    <span className="flex-1 text-left">{entry.label}</span>
                    <span style={{ color: "var(--text-faint)" }}>
                      <Icon name="chevron" className="!h-4 !w-4" />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          );
        })}
      </nav>

      <div className="border-t px-4 py-2.5 text-[11px]" style={{ borderColor: "var(--border)", color: "var(--text-faint)" }}>
        ローカル版 · 単一ユーザー
      </div>

      {/* freee型 サブメニュー・フライアウト */}
      {flyout && (
        <div
          className="fixed z-50"
          style={{
            top: flyout.top - 12,
            left: flyout.anchorRight,
            width: Math.max(flyout.left - flyout.anchorRight, 0),
            height: 12 + 32 + flyout.entry.children.length * 34 + 24,
          }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />
      )}
      {flyout && (
        <div
          className="fixed z-50 min-w-[220px] overflow-hidden rounded-[var(--radius)] border py-1.5"
          style={{
            top: flyout.top,
            left: flyout.left + 4,
            background: "var(--surface)",
            borderColor: "var(--border)",
            boxShadow: "var(--shadow-md, 0 10px 30px rgba(0,0,0,0.14))",
          }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="px-3.5 pb-1 pt-1 text-[10.5px] font-semibold tracking-wider" style={{ color: "var(--text-faint)" }}>
            {flyout.entry.label}
          </div>
          <div className="flex flex-col">
            {flyout.entry.children.map((c, ci) => {
              if (c.disabled) {
                return (
                  <span
                    key={ci}
                    className="flex cursor-not-allowed items-center justify-between gap-3 px-3.5 py-1.5 text-[12.5px]"
                    style={{ color: "var(--text-faint)" }}
                    title={c.note ? `${c.note}（ローカル版では利用できません）` : undefined}
                  >
                    <span>{c.label}</span>
                    {c.note && (
                      <span className="shrink-0 rounded px-1 py-0.5 text-[9.5px]" style={{ background: "var(--surface-2)" }}>
                        {c.note}
                      </span>
                    )}
                  </span>
                );
              }
              const isActive = active === c.href;
              return (
                <Link
                  key={c.href}
                  href={c.href}
                  onClick={() => setFlyout(null)}
                  className="px-3.5 py-1.5 text-[12.5px] transition-colors hover:bg-[var(--surface-2)]"
                  style={{
                    color: isActive ? "var(--accent-strong)" : "var(--text)",
                    background: isActive ? "var(--accent-soft)" : "transparent",
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {c.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}
